'use client';

import React, { useMemo } from 'react';
import type { Reminder } from '@/core/domain';
import AppIcon from '@/core/presentation/components/ui/AppIcon';
import {
  DASHBOARD_ALERTS_EMPTY,
  DASHBOARD_ALERTS_TITLE,
  DASHBOARD_KPI_CONTACTS_OVERDUE,
  DASHBOARD_KPI_CONVERSION,
  DASHBOARD_KPI_MANUT_TODAY,
  DASHBOARD_KPI_REMINDERS_WEEK,
  DASHBOARD_KPI_REVENUE_MONTH,
  DASHBOARD_PIPELINE_AGENDADO,
  DASHBOARD_PIPELINE_CONTATADO,
  DASHBOARD_PIPELINE_PENDENTE,
  DASHBOARD_TREND_VS_PRIOR_MONTH,
  DASHBOARD_TREND_VS_PRIOR_WEEK,
  DASHBOARD_UPCOMING_LEADS_EMPTY,
  DASHBOARD_UPCOMING_LEADS_TITLE,
} from '@/features/app/dashboard/presentation/constants/dashboard-copy';
import { DashboardKpiCard } from '@/features/app/dashboard/presentation/components/DashboardKpiCard';
import { DashboardWeekAgenda } from '@/features/app/dashboard/presentation/components/DashboardWeekAgenda';
import { localTodayDateKey } from '@/features/app/presentation/utils/calendar-day-cell';
import { isoToDateKey } from '@/features/app/presentation/utils/reminder-day-metrics';
import {
  conversionContactedToScheduledForMonth,
  countContactsOverdue,
  countMaintenancesInDateRange,
  countMaintenancesOnDay,
  countSendEventsInDateRange,
  currentMonthRef,
  dashboardSendFailureAlerts,
  localThisAndPrevWeekBounds,
  metricsForMonth,
  nextFiveByExpectedDate,
  parseMonthRef,
  percentChangeRatio,
  pipelineStatusFromReminder,
  type DashboardPipelineLabel,
} from '@/features/app/dashboard/presentation/utils/dashboard-metrics';

function formatDateKeyShort(dateKey: string): string {
  return new Date(`${dateKey}T12:00:00`).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatMoney(n: number): string {
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

const PIPELINE_LABEL: Record<DashboardPipelineLabel, string> = {
  pendente: DASHBOARD_PIPELINE_PENDENTE,
  contatado: DASHBOARD_PIPELINE_CONTATADO,
  agendado: DASHBOARD_PIPELINE_AGENDADO,
};

const PIPELINE_BADGE: Record<DashboardPipelineLabel, string> = {
  pendente: 'bg-amber-500/12 text-amber-800',
  contatado: 'bg-sky-500/12 text-sky-800',
  agendado: 'bg-emerald-500/12 text-emerald-800',
};

export interface DashboardOverviewTabProps {
  reminders: Reminder[];
  getClientName: (clientId: string) => string;
  /** Quando true, a coluna de alertas de falha de envio fica vazia (ex.: dashboard com dados mockados). */
  suppressSendFailureAlerts?: boolean;
}

export const DashboardOverviewTab: React.FC<DashboardOverviewTabProps> = ({
  reminders,
  getClientName,
  suppressSendFailureAlerts = false,
}) => {
  const todayKey = localTodayDateKey();

  const kpi = useMemo(() => {
    const w = localThisAndPrevWeekBounds();
    const maintToday = countMaintenancesOnDay(reminders, todayKey);
    const maintThisWeek = countMaintenancesInDateRange(reminders, w.thisStart, w.thisEnd);
    const maintPrevWeek = countMaintenancesInDateRange(reminders, w.prevStart, w.prevEnd);
    const trendMaintWeek = percentChangeRatio(maintThisWeek, maintPrevWeek);

    const contactsOverdue = countContactsOverdue(reminders, todayKey);

    const sendsThisWeek = countSendEventsInDateRange(reminders, w.thisStart, w.thisEnd);
    const sendsPrevWeek = countSendEventsInDateRange(reminders, w.prevStart, w.prevEnd);
    const trendSendsWeek = percentChangeRatio(sendsThisWeek, sendsPrevWeek);

    const ref = currentMonthRef();
    const parsed = parseMonthRef(ref);
    let conv: number | null = null;
    let convPrev: number | null = null;
    let revenue = 0;
    let revenuePrev = 0;
    if (parsed) {
      const { year, monthIndex } = parsed;
      conv = conversionContactedToScheduledForMonth(reminders, year, monthIndex);
      let py = year;
      let pm = monthIndex - 1;
      if (pm < 0) {
        pm = 11;
        py -= 1;
      }
      convPrev = conversionContactedToScheduledForMonth(reminders, py, pm);
      revenue = metricsForMonth(reminders, year, monthIndex).revenue;
      revenuePrev = metricsForMonth(reminders, py, pm).revenue;
    }
    const trendConvPoints =
      conv != null && convPrev != null ? Math.round(conv - convPrev) : null;
    const trendRevenue = percentChangeRatio(revenue, revenuePrev);

    return {
      maintToday,
      trendMaintWeek,
      contactsOverdue,
      sendsThisWeek,
      trendSendsWeek,
      conv,
      trendConvPoints,
      revenue,
      trendRevenue,
    };
  }, [reminders, todayKey]);

  const nextLeads = useMemo(
    () => nextFiveByExpectedDate(reminders, todayKey, 5),
    [reminders, todayKey]
  );

  const failureAlerts = useMemo(() => {
    if (suppressSendFailureAlerts) return [];
    return dashboardSendFailureAlerts(reminders, getClientName);
  }, [reminders, getClientName, suppressSendFailureAlerts]);

  return (
    <div className="space-y-6 min-w-0">
      <div className="grid grid-cols-2 items-stretch gap-2 sm:gap-3 xl:grid-cols-5 xl:gap-4">
        <DashboardKpiCard
          variant="gradient"
          iconName="CalendarDaysIcon"
          title={DASHBOARD_KPI_MANUT_TODAY}
          value={kpi.maintToday}
          trend={
            kpi.trendMaintWeek != null ?
              { value: kpi.trendMaintWeek, label: DASHBOARD_TREND_VS_PRIOR_WEEK, mode: 'ratio' }
            : null
          }
        />
        <DashboardKpiCard
          iconName="UserMinusIcon"
          title={DASHBOARD_KPI_CONTACTS_OVERDUE}
          value={kpi.contactsOverdue}
        />
        <DashboardKpiCard
          iconName="ChatBubbleLeftRightIcon"
          title={DASHBOARD_KPI_REMINDERS_WEEK}
          value={kpi.sendsThisWeek}
          trend={
            kpi.trendSendsWeek != null ?
              { value: kpi.trendSendsWeek, label: DASHBOARD_TREND_VS_PRIOR_WEEK, mode: 'ratio' }
            : null
          }
        />
        <DashboardKpiCard
          iconName="CheckCircleIcon"
          title={DASHBOARD_KPI_CONVERSION}
          value={kpi.conv == null ? '—' : `${kpi.conv}%`}
          trend={
            kpi.trendConvPoints != null && kpi.trendConvPoints !== 0 ?
              {
                value: kpi.trendConvPoints,
                label: DASHBOARD_TREND_VS_PRIOR_MONTH,
                mode: 'points',
              }
            : null
          }
        />
        <DashboardKpiCard
          className="col-span-2 xl:col-span-1"
          iconName="BanknotesIcon"
          title={DASHBOARD_KPI_REVENUE_MONTH}
          value={formatMoney(kpi.revenue)}
          trend={
            kpi.trendRevenue != null ?
              { value: kpi.trendRevenue, label: DASHBOARD_TREND_VS_PRIOR_MONTH, mode: 'ratio' }
            : null
          }
        />
      </div>

      <DashboardWeekAgenda reminders={reminders} getClientName={getClientName} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-border-light bg-white p-5 shadow-sm">
          <h3 className="font-semibold text-navy-dark mb-4 flex items-center gap-2">
            <AppIcon name="ClockIcon" size={18} className="text-cyan shrink-0" />
            {DASHBOARD_UPCOMING_LEADS_TITLE}
          </h3>
          {nextLeads.length === 0 ? (
            <p className="text-sm text-text-muted py-6 text-center">{DASHBOARD_UPCOMING_LEADS_EMPTY}</p>
          ) : (
            <div className="space-y-2">
              {nextLeads.map((r) => {
                const pipe = pipelineStatusFromReminder(r);
                return (
                  <div
                    key={r.id}
                    className="flex items-center justify-between gap-3 rounded-lg bg-[#f0f4f8]/80 px-4 py-2.5"
                  >
                    <span className="text-sm font-medium text-navy-dark truncate min-w-0">
                      {getClientName(r.client_id)}
                    </span>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-xs text-text-muted tabular-nums">
                        {formatDateKeyShort(isoToDateKey(r.expected_date))}
                      </span>
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${PIPELINE_BADGE[pipe]}`}
                      >
                        {PIPELINE_LABEL[pipe]}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-border-light bg-white p-5 shadow-sm">
          <h3 className="font-semibold text-navy-dark mb-4 flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" />
            </span>
            {DASHBOARD_ALERTS_TITLE}
          </h3>
          {failureAlerts.length === 0 ? (
            <p className="text-sm text-text-muted py-6 text-center">{DASHBOARD_ALERTS_EMPTY}</p>
          ) : (
            <div className="space-y-2">
              {failureAlerts.map((a) => (
                <div
                  key={a.reminderId}
                  className="flex gap-3 rounded-lg border border-border-light px-4 py-3 bg-[#fafbfc]"
                >
                  <AppIcon name="ExclamationCircleIcon" size={20} className="text-red-500 shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-navy-dark leading-snug">{a.message}</p>
                    <p className="text-xs text-text-muted mt-1">{a.timeLabel}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
